import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  FREE_REACTIONS,
  REACTION_TONE_STYLES,
  FREE_REACTION_BY_EMOJI,
  getReactionAbbrev,
  getActiveWorkflowByCategory,
  getMergedWorkflowCategories,
  searchReactions,
  toReactionList,
  WORK_ITEM_TYPE_TASK_ID,
} from '../../utils/reactionConfig';

const TABS = [
  { id: 'free', label: 'Free' },
  { id: 'workflow', label: 'Workflow' },
];

const PICKER_WIDTH = 'w-[300px] sm:w-[360px]';
const FREE_GRID_CLASS = 'grid grid-cols-6 gap-0.5';
const WORKFLOW_GRID_CLASS = 'grid grid-cols-3 gap-1';

const FreeReactionTile = ({ reaction, onSelect, isActive }) => (
  <button
    type="button"
    onClick={() => onSelect(reaction.id)}
    className={`p-1.5 rounded text-lg leading-none transition-colors flex items-center justify-center min-w-0 ${
      isActive
        ? 'bg-primary-100 dark:bg-primary-900/40 ring-2 ring-primary-400/70 dark:ring-primary-500/60'
        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
    }`}
    title={reaction.label}
    aria-label={reaction.label}
    aria-pressed={isActive}
  >
    <span role="img" aria-label={reaction.label}>
      {reaction.emoji}
    </span>
  </button>
);

const WorkflowReactionTile = ({ reaction, onSelect, isActive, disabled = false, disabledReason }) => {
  const toneClass = REACTION_TONE_STYLES[reaction.tone]?.picker || REACTION_TONE_STYLES.slate.picker;
  const abbrev = getReactionAbbrev(reaction.label);
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={() => {
        if (disabled) return;
        onSelect(reaction.id);
      }}
      disabled={disabled}
      className={`flex flex-col items-center justify-center gap-0.5 p-1.5 min-h-[52px] rounded-md border text-center transition-colors min-w-0 ${toneClass} ${
        isActive ? 'ring-2 ring-primary-500/70 dark:ring-primary-400/80' : ''
      } ${disabled ? 'opacity-45 cursor-not-allowed' : ''}`}
      title={disabled && disabledReason ? disabledReason : reaction.label}
      aria-label={reaction.label}
      aria-pressed={isActive}
      aria-disabled={disabled}
    >
      <span className="text-base leading-none flex-shrink-0" role="img" aria-label={reaction.label}>
        {reaction.emoji}
      </span>
      {isActive ? (
        <span className="text-[9px] font-bold uppercase tracking-wide leading-none">
          {abbrev}
        </span>
      ) : (
        <span className="text-[9px] font-medium leading-tight line-clamp-2 w-full px-0.5">
          {reaction.label}
        </span>
      )}
    </button>
  );
};

const ReactionPicker = ({
  onSelect,
  currentReactions = [],
  currentUserId,
  className = '',
  style,
  initialTab = 'free',
  hideCategories = [],
}) => {
  const customItems = useSelector((state) => state.workflowCatalog?.items || []);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const hiddenCategories = useMemo(() => new Set(hideCategories), [hideCategories]);
  const workflowCategories = useMemo(
    () => getMergedWorkflowCategories(customItems),
    [customItems],
  );
  const visibleWorkflowCategories = useMemo(
    () => workflowCategories.filter((category) => !hiddenCategories.has(category.id)),
    [hiddenCategories, workflowCategories],
  );

  const activeWorkflowByCategory = useMemo(
    () => getActiveWorkflowByCategory(currentReactions),
    [currentReactions],
  );

  const userFreeReactionIds = useMemo(() => {
    if (!currentUserId) return new Set();
    const ids = new Set();
    (toReactionList(currentReactions)).forEach((reaction) => {
      if (!reaction?.emoji || reaction.emoji.startsWith('wf:')) return;
      const hasUser = Array.isArray(reaction.userIds)
        && reaction.userIds.some((id) => String(id) === String(currentUserId));
      if (!hasUser) return;
      ids.add(reaction.emoji);
      const mapped = FREE_REACTION_BY_EMOJI[reaction.emoji];
      if (mapped) ids.add(mapped.id);
    });
    return ids;
  }, [currentReactions, currentUserId]);

  const lockedWorkItemType = activeWorkflowByCategory['work-item-type'] || null;

  const workItemTypeDisable = (reaction) => {
    if (reaction.category !== 'work-item-type') {
      return { disabled: false, reason: undefined };
    }
    // Cannot convert an existing workflow to Task.
    if (lockedWorkItemType && reaction.id === WORK_ITEM_TYPE_TASK_ID) {
      return { disabled: true, reason: 'Cannot change a workflow to a task' };
    }
    return { disabled: false, reason: undefined };
  };

  const isFreeReactionActive = (reaction) =>
    userFreeReactionIds.has(reaction.id) || userFreeReactionIds.has(reaction.emoji);

  const searchResults = useMemo(
    () => searchReactions(searchQuery, { hideCategories, categories: workflowCategories }),
    [searchQuery, hideCategories, workflowCategories],
  );

  const isSearching = searchQuery.trim().length > 0;
  const hasSearchResults = searchResults.workflow.length > 0 || searchResults.free.length > 0;

  return (
    <div
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden ${PICKER_WIDTH} ${className}`}
      style={style}
      data-reaction-picker
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="p-2 border-b border-gray-200 dark:border-gray-600 space-y-1.5">
        <div className="relative">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search reactions…"
            className="w-full pl-8 pr-8 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
            autoComplete="off"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              aria-label="Clear search"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {!isSearching && (
          <p className="text-[10px] text-gray-500 dark:text-gray-400 px-0.5">
            {activeTab === 'free'
              ? 'Multiple allowed · tap again to remove yours'
              : 'One status per group · anyone can update'}
          </p>
        )}
      </div>

      {isSearching ? (
        <div className="p-2 max-h-64 overflow-y-auto space-y-3">
          {!hasSearchResults ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-6">
              No reactions match &ldquo;{searchQuery.trim()}&rdquo;
            </p>
          ) : (
            <>
              {searchResults.free.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5 px-0.5">
                    Free ({searchResults.free.length})
                  </p>
                  <div className={FREE_GRID_CLASS}>
                    {searchResults.free.map((reaction) => (
                      <FreeReactionTile
                        key={reaction.id}
                        reaction={reaction}
                        onSelect={onSelect}
                        isActive={isFreeReactionActive(reaction)}
                      />
                    ))}
                  </div>
                </div>
              )}
              {searchResults.workflow.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5 px-0.5">
                    Workflow ({searchResults.workflow.length})
                  </p>
                  <div className={WORKFLOW_GRID_CLASS}>
                    {searchResults.workflow.map((reaction) => {
                      const { disabled, reason } = workItemTypeDisable(reaction);
                      return (
                        <WorkflowReactionTile
                          key={reaction.id}
                          reaction={reaction}
                          onSelect={onSelect}
                          isActive={activeWorkflowByCategory[reaction.category] === reaction.id}
                          disabled={disabled}
                          disabledReason={reason}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <>
          <div className="flex border-b border-gray-200 dark:border-gray-600">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/25 border-b-2 border-primary-500 -mb-px'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'free' ? (
            <div className="p-2 max-h-56 overflow-y-auto">
              <div className={FREE_GRID_CLASS}>
                {FREE_REACTIONS.map((reaction) => (
                  <FreeReactionTile
                    key={reaction.id}
                    reaction={reaction}
                    onSelect={onSelect}
                    isActive={isFreeReactionActive(reaction)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="p-2 max-h-72 overflow-y-auto space-y-3">
              {visibleWorkflowCategories.map((category) => (
                <div key={category.id}>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0.5 px-0.5 sticky top-0 bg-white dark:bg-gray-800 py-0.5 z-[1]">
                    {category.label}
                  </p>
                  <p className="text-[9px] text-gray-400 dark:text-gray-500 mb-1.5 px-0.5">
                    {category.hint}
                  </p>
                  <div className={WORKFLOW_GRID_CLASS}>
                    {category.reactions.map((reaction) => {
                      const { disabled, reason } = workItemTypeDisable(reaction);
                      return (
                        <WorkflowReactionTile
                          key={reaction.id}
                          reaction={reaction}
                          onSelect={onSelect}
                          isActive={activeWorkflowByCategory[category.id] === reaction.id}
                          disabled={disabled}
                          disabledReason={reason}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReactionPicker;
